import React from 'react';
import { Link } from 'react-router-dom';

/**
 * Converts wiki entry titles in text to clickable links
 * @param {string} text - The text to process
 * @param {Array} wikiEntries - Array of wiki entries with {id, title, type}
 * @returns {Array} Array of React elements (text and links)
 */
export const linkifyWikiEntries = (text, wikiEntries) => {
  if (!text || !wikiEntries || wikiEntries.length === 0) {
    return text;
  }

  // Sort entries by title length (longest first) to match longer phrases first
  const sortedEntries = [...wikiEntries].sort((a, b) => b.title.length - a.title.length);

  // Create a map of positions where links should be inserted
  const matches = [];
  
  sortedEntries.forEach(entry => {
    if (!entry.title) return;
    
    // Case-insensitive search for exact title matches (word boundaries)
    const regex = new RegExp(`\\b${escapeRegex(entry.title)}\\b`, 'gi');
    let match;
    
    while ((match = regex.exec(text)) !== null) {
      const matchStart = match.index;
      const matchEnd = match.index + match[0].length;
      const matchText = match[0];
      
      // Check if this position overlaps with an existing match
      const overlaps = matches.some(m => 
        (matchStart >= m.start && matchStart < m.end) ||
        (matchEnd > m.start && matchEnd <= m.end)
      );
      
      if (!overlaps) {
        matches.push({
          start: matchStart,
          end: matchEnd,
          text: matchText,
          entry: entry
        });
      }
    }
  });

  // Sort matches by position
  matches.sort((a, b) => a.start - b.start);

  // If no matches, return original text
  if (matches.length === 0) {
    return text;
  }

  // Build the result with text and link elements
  const result = [];
  let lastIndex = 0;

  matches.forEach((match, index) => {
    // Add text before the match
    if (match.start > lastIndex) {
      result.push(text.substring(lastIndex, match.start));
    }

    // Add the link
    result.push(
      <Link
        key={`wiki-link-${match.entry.id}-${index}`}
        to={`/wiki/${match.entry.id}`}
        style={{
          color: '#B08D57',
          fontWeight: 'bold',
          textDecoration: 'underline',
          cursor: 'pointer'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {match.text}
      </Link>
    );

    lastIndex = match.end;
  });

  // Add remaining text
  if (lastIndex < text.length) {
    result.push(text.substring(lastIndex));
  }

  return result;
};

/**
 * Escape special regex characters in a string
 */
const escapeRegex = (string) => {
  return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
};

/**
 * Get icon emoji based on wiki entry type
 */
export const getWikiIcon = (type) => {
  switch (type) {
    case 'person':
      return '👥';
    case 'location':
      return '📍';
    case 'item':
      return '⚔️';
    case 'monster':
      return '👹';
    default:
      return '📖';
  }
};
